import React, { useCallback, useMemo } from 'react';
import { useTranslation } from 'react-i18next';

import { CLARITY_MASK_PROPS } from 'constants/clarity.constants';
import { useSqToast } from 'context';
import { FormFieldType, RequisitesType } from 'enums';
import { useOrderContext } from 'hooks';
import { TranslationNamespace } from 'i18n';
import { clipboardUtils, formatUtils } from 'utils';

import { SqCountryFlag } from '../SqCountryFlag';
import { SqLogoImage } from '../SqLogoImage';

type Props = {
  label: string;
  value: string;
  name?: string;
  type?: string;
  copyValue?: string;
};

const NOT_COPYABLE_FIELDS = new Set([
  'bank',
  'countryCode',
  'accountLastDigits',
  'expirationDate',
]);

// names are pasted into bank apps as is, so their spaces are kept
const NAME_FIELDS = new Set([
  'cardholder',
  'firstName',
  'lastName',
  'middleName',
]);

const CopyIcon: React.FC = () => (
  <svg width="24" height="24" viewBox="0 0 24 24" fill="none">
    <path
      d="M2 12C2 9.79086 3.79086 8 6 8H12C14.2091 8 16 9.79086 16 12V18C16 18.2761 15.972 18.5457 15.9187 18.8061C15.5582 20.5679 14.0396 21.9077 12.193 21.9954C12.129 21.9985 12.0647 22 12 22H6C5.93531 22 5.87098 21.9985 5.80704 21.9954C3.75145 21.8978 2.10221 20.2485 2.00457 18.193C2.00154 18.129 2 18.0647 2 18V12Z"
      fill="#0162FF"
    />
    <path
      d="M18 2H12C9.79086 2 8 3.79086 8 6V7H12C14.7614 7 17 9.23858 17 12V16H18C20.2091 16 22 14.2091 22 12V6C22 3.79086 20.2091 2 18 2Z"
      fill="#0162FF"
      fillOpacity="0.2"
    />
  </svg>
);

export const SqFieldCard: React.FC<Props> = ({
  label,
  value,
  name,
  type = '',
  copyValue: copyValueProp,
}) => {
  const { showCopiedToast, showToast } = useSqToast();
  const { t: tToast } = useTranslation(TranslationNamespace.Common, {
    keyPrefix: 'components.sq_toast',
  });
  const {
    requisitesBank,
    requisitesCountryCode,
    paymentTypeFlags: { isMobileOperator },
  } = useOrderContext();

  const isBankField = type === FormFieldType.Bank;
  const isCountryField = type === FormFieldType.Country;

  const displayValue = useMemo(() => {
    if (type === FormFieldType.Phone) {
      return formatUtils.formatPhone(value);
    }
    if (type === RequisitesType.CardNumber) {
      return formatUtils.formatCardNumber(value);
    }
    return value;
  }, [type, value]);

  const computedCopyValue = useMemo(() => {
    if (!name || NOT_COPYABLE_FIELDS.has(name)) {
      return undefined;
    }
    return NAME_FIELDS.has(name) ? value.trim() : value.replace(/\s/g, '');
  }, [name, value]);

  const copyValue = copyValueProp ?? computedCopyValue;

  const handleCopy = useCallback(() => {
    if (!copyValue) {
      return;
    }
    clipboardUtils.copyToClipboardWithFeedback(
      copyValue,
      () => showCopiedToast(label),
      () => showToast(tToast('copy_failed')),
    );
  }, [copyValue, label, showCopiedToast, showToast, tToast]);

  const isCopyAvailable = !!navigator?.clipboard?.writeText && !!copyValue;

  return (
    <div>
      <div className="tw-text-sm tw-text-secondary tw-mb-1 tw-pl-4">
        {label}
      </div>
      <div className="tw-flex tw-items-center tw-justify-between tw-bg-payment-layout-out tw-rounded-full tw-px-6 tw-py-4">
        <div className="tw-flex tw-items-center tw-gap-2 tw-min-w-0">
          {isBankField && !isMobileOperator && (
            <SqLogoImage
              bank={requisitesBank}
              bankName={value}
              className="tw-w-6 tw-h-6 tw-shrink-0"
            />
          )}
          {isCountryField && (
            <SqCountryFlag
              countryCode={requisitesCountryCode}
              className="!tw-w-6 !tw-h-4.5 tw-shrink-0"
            />
          )}
          <span
            className="tw-text-base tw-font-medium tw-text-primary tw-truncate"
            {...CLARITY_MASK_PROPS}
          >
            {displayValue}
          </span>
        </div>
        {isCopyAvailable && (
          <button
            className="tw-flex tw-items-center tw-justify-center tw-bg-transparent tw-border-none tw-cursor-pointer tw-shrink-0 tw-ml-2 tw-p-0"
            onClick={handleCopy}
          >
            <CopyIcon />
          </button>
        )}
      </div>
    </div>
  );
};
